import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

import { useSanityData } from '../../../Context/SanityDataContext';
import { useSanityLocalized } from '../../../hooks/useSanityLocalized';
import { theme } from '../../../theme';
import LanguageToggle from '../../ui/LanguageToggle';

interface HeaderProps {
  onAboutClick: () => void;
  onCareerClick: () => void;
  onProjectsClick: () => void;
  onContactClick: () => void;
  onJobAnalysisClick?: () => void;
  activeTabs: { [key: string]: boolean };
}

interface NavOption {
  key: string;
  label: string;
  command: string;
  icon: React.ReactNode;
  onClick: () => void;
}

const Container = styled.div`
  font-weight: 600;
  width: 100%;
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0;
  left: 0;
  padding: 20px;
  z-index: 90;
  font-family: 'Courier New', monospace;
  color: ${theme.colors.green};

  @media (max-width: 600px) {
    padding: 15px;
  }
`;

const NameText = styled.h1`
  font-size: 28px;
  margin: 0;
  color: white;

  @media (max-width: 600px) {
    font-size: 22px;
  }
`;

const TerminalNav = styled.div`
  display: flex;
  margin-top: 20px;
  flex-direction: column;
  align-items: flex-start;
  max-width: 300px;
`;

const Prompt = styled.div`
  display: flex;
  align-items: center;
  margin-right: 10px;
  margin-bottom: 10px;
  color: ${theme.colors.green};
  font-size: 14px;

  .user {
    color: #4caf50;
  }

  .at {
    color: white;
  }

  .host {
    color: #2196f3;
  }

  .path {
    color: #ffc107;
  }

  .dollar {
    color: white;
    margin: 0 5px;
  }

  svg {
    margin-right: 5px;
    font-size: 18px;
  }
`;

const typeEffect = keyframes`
  from { width: 0 }
  to { width: 100% }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const pulse = keyframes`
  0%, 100% { 
    box-shadow: 0 0 0 rgba(31, 207, 94, 0.4);
  }
  50% { 
    box-shadow: 0 0 10px rgba(31, 207, 94, 0.7);
  }
`;

const CommandInput = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;
  padding: 8px 10px;
  background-color: rgba(0, 20, 0, 0.8);

  border-radius: 2px;
  color: ${theme.colors.green};
  font-family: 'Courier New', monospace;
  font-size: 14px;
  margin-bottom: 8px;
  margin-top: 4px;
  transition: all 0.2s ease;
`;

const CommandsContainer = styled.div`
  width: 100%;
  max-width: 300px;
  margin-top: 5px;
  background-color: rgba(0, 20, 0, 0.7);
  border: 1px solid ${theme.colors.green}50;
  border-radius: 3px;
  overflow: hidden;
  font-family: 'Courier New', monospace;
  color: ${theme.colors.green};
  margin-bottom: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
`;

const CommandLine = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  padding: 10px 8px;
  cursor: pointer;
  color: ${props => (props.$active ? theme.colors.green : 'rgba(255, 255, 255, 0.8)')};
  border-bottom: 1px solid rgba(31, 207, 94, 0.1);
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: rgba(31, 207, 94, 0.1);
  }

  .prefix {
    color: ${theme.colors.green};
    margin-right: 8px;
    opacity: 0.8;
  }

  svg {
    margin-right: 8px;
    font-size: 16px;
  }

  .command {
  }

  ${props =>
    props.$active &&
    `
        background-color: rgba(31, 207, 94, 0.1);
    `}
`;

const Subtitle = styled.div`
  font-size: 16px;
  margin-top: 5px;
  color: ${theme.colors.green};
  min-height: 24px;

  @media (max-width: 600px) {
    font-size: 14px;
    margin-top: 3px;
  }
`;

const SubtitleInput = styled.input`
  background: transparent;
  border: none;
  font-family: 'Courier New', monospace;
  font-size: 16px;
  color: ${theme.colors.green};
  outline: none;
  width: 100%;
  padding: 0;
  margin: 0;
  caret-color: ${theme.colors.green};

  @media (max-width: 600px) {
    font-size: 14px;
  }
`;

const CommandText = styled.span<{ $isVisible: boolean }>`
  display: ${props => (props.$isVisible ? 'inline-block' : 'none')};
  overflow: hidden;
  white-space: nowrap;
  animation: ${typeEffect} 0.8s steps(30, end);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    right: -3px;
    top: 2px;
    width: 1px;
    height: 14px;
    background-color: ${theme.colors.green};
    animation: ${blink} 1s step-end infinite;
  }
`;

const ProfileButton = styled.div`
  position: absolute;
  top: 20px;
  right: 70px; /* Move left to make room for language button */
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 4px;
  background-color: rgba(0, 20, 0, 0.7);
  border: 1px solid rgba(31, 207, 94, 0.4);
  color: ${theme.colors.green};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  z-index: 95;

  &:hover {
    background-color: rgba(31, 207, 94, 0.1);
  }

  @media (max-width: 600px) {
    top: 15px;
    right: 65px; /* Adjust for mobile */
    padding: 6px 10px;
    font-size: 12px;
  }
`;

const LanguageContainer = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 100;

  @media (max-width: 600px) {
    top: 15px;
    right: 15px;
  }
`;

const Header: React.FC<HeaderProps> = ({
  onAboutClick,
  onCareerClick,
  onProjectsClick,
  onContactClick,
  onJobAnalysisClick,
  activeTabs,
}) => {
  const { t } = useTranslation();
  const { person } = useSanityData();
  const { getLocalizedValue } = useSanityLocalized();

  // Get name and title from Sanity, with fallbacks
  const personName = person?.name || 'Arian Garshi';
  const firstName = personName.split(' ')[0].toLowerCase();
  const personTitle = getLocalizedValue(person?.title, t('modals.header.subtitle'));

  const [subtitle, setSubtitle] = useState(personTitle);
  const [activeCommand, setActiveCommand] = useState<string | null>(null);
  const [commandText, setCommandText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Update subtitle when personTitle changes (e.g., language switch)
  useEffect(() => {
    setSubtitle(personTitle);
  }, [personTitle]);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const navOptions: NavOption[] = [
    {
      key: 'about',
      label: t('nav.about'),
      command: t('nav.about'),
      icon: <PersonIcon />,
      onClick: onAboutClick,
    },
    {
      key: 'career',
      label: t('nav.career'),
      command: t('nav.career'),
      icon: <WorkHistoryIcon />,
      onClick: onCareerClick,
    },
    {
      key: 'projects',
      label: t('nav.projects'),
      command: t('nav.projects'),
      icon: <WorkIcon />,
      onClick: onProjectsClick,
    },
    {
      key: 'contact',
      label: t('nav.contact'),
      command: t('nav.contact'),
      icon: <EmailIcon />,
      onClick: onContactClick,
    },
  ];

  // Add Job Analysis option if callback is provided
  if (onJobAnalysisClick) {
    navOptions.push({
      key: 'jobanalysis',
      label: t('nav.jobAnalysis'),
      command: t('nav.jobAnalysis'),
      icon: <WorkIcon />,
      onClick: onJobAnalysisClick,
    });
  }

  const handleSubtitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSubtitle(e.target.value);
  };

  const handleSubtitleClick = (e: React.MouseEvent) => {
    // Prevent selection on click
    e.preventDefault();
    if (inputRef.current) {
      inputRef.current.focus();
      // Move cursor to end of text
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);
    }
  };

  const executeCommand = (option: NavOption) => {
    setActiveCommand(option.key);
    setCommandText(option.command);

    // Show typing effect first, then execute
    setTimeout(() => {
      option.onClick();

      // Reset after a delay
      setTimeout(() => {
        setCommandText('');
        setActiveCommand(null);
      }, 800);
    }, 500);
  };

  const handleLoginClick = () => {
    // Auth functionality removed - placeholder for future implementation
    console.log('Login functionality removed');
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle number keys when not typing in the input
      if (document.activeElement !== inputRef.current) {
        if (e.key === '1') {
          onAboutClick();
        } else if (e.key === '2') {
          onCareerClick();
        } else if (e.key === '3') {
          onProjectsClick();
        } else if (e.key === '4') {
          onContactClick();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onAboutClick, onCareerClick, onProjectsClick, onContactClick]);

  return (
    <Container>
      <LanguageContainer>
        <LanguageToggle />
      </LanguageContainer>

      <NameText>{personName}</NameText>
      <Subtitle onClick={handleSubtitleClick}>
        <SubtitleInput
          ref={inputRef}
          value={subtitle}
          onChange={handleSubtitleChange}
          spellCheck={false}
        />
      </Subtitle>

      <TerminalNav>
        <Prompt>
          <span className="user">{firstName}</span>
          <span className="at">@</span>
          <span className="host">portfolio</span>
          <span className="at">:</span>
          <span className="path">~</span>
          <span className="dollar">$</span>
        </Prompt>

        <CommandsContainer>
          {navOptions.map(option => (
            <CommandLine
              key={option.key}
              $active={activeTabs[option.key]}
              onClick={() => executeCommand(option)}
            >
              <span className="prefix">$</span>
              {option.icon}
              <span className="command">{option.command}</span>
            </CommandLine>
          ))}
        </CommandsContainer>
        <CommandInput>
          <CommandText $isVisible={activeCommand === null}>
            {t('modals.header.commandText')}
          </CommandText>
          <CommandText $isVisible={activeCommand !== null}>{commandText}</CommandText>
        </CommandInput>
      </TerminalNav>
    </Container>
  );
};

export default Header;
